/**
 * Random Verse Dialog
 * Shows a random verse with translation when triggered
 */

import { useState, useCallback } from 'react';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Shuffle, RefreshCw } from 'lucide-react';
import { getRandomVerse } from '@/services/quran.service';
import type { Ayah } from '@/types/quran';

interface RandomVerseDialogProps {
  open: boolean;
  onClose: () => void;
  translationId?: number;
}

export function RandomVerseDialog({ open, onClose, translationId }: RandomVerseDialogProps) {
  const [verse, setVerse] = useState<Ayah | null>(null);
  const [loading, setLoading] = useState(false);

  const fetchRandom = useCallback(async () => {
    setLoading(true);
    try {
      const v = await getRandomVerse(translationId ? [translationId] : undefined);
      setVerse(v);
    } catch {
      setVerse(null);
    } finally {
      setLoading(false);
    }
  }, [translationId]);

  // Fetch on first open
  const handleOpenChange = (isOpen: boolean) => {
    if (isOpen && !verse) fetchRandom();
    if (!isOpen) onClose();
  };

  return (
    <Sheet open={open} onOpenChange={handleOpenChange}>
      <SheetContent side="bottom" className="max-h-[70vh] overflow-y-auto rounded-t-2xl">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2">
            <Shuffle className="w-5 h-5" />
            อายะฮ์สุ่ม
          </SheetTitle>
          <SheetDescription>
            {verse && `${verse.surahNumber}:${verse.numberInSurah}`}
          </SheetDescription>
        </SheetHeader>

        <div className="mt-4 space-y-4">
          {loading ? (
            <div className="space-y-3">
              <Skeleton className="h-16 w-full rounded-lg" />
              <Skeleton className="h-4 w-full" />
              <Skeleton className="h-4 w-3/4" />
            </div>
          ) : verse ? (
            <>
              <div
                className="p-4 rounded-lg bg-muted/50 font-amiri-quran text-2xl text-right leading-loose"
                dir="rtl"
              >
                {verse.textTajweed ? (
                  <span dangerouslySetInnerHTML={{ __html: verse.textTajweed }} />
                ) : (
                  verse.text
                )}
              </div>

              {verse.translations && verse.translations.length > 0 && (
                <div className="p-3 rounded-lg border text-sm leading-relaxed">
                  {verse.translations[0].text}
                  {verse.translations[0].resourceName && (
                    <p className="text-xs text-muted-foreground mt-2">
                      — {verse.translations[0].resourceName}
                    </p>
                  )}
                </div>
              )}

              <p className="text-xs text-muted-foreground text-center">
                ซูเราะฮ์ที่ {verse.surahNumber} อายะฮ์ที่ {verse.numberInSurah} · หน้า {verse.page} · ญุซอ์ {verse.juz}
              </p>
            </>
          ) : (
            <p className="text-sm text-muted-foreground">ไม่สามารถโหลดอายะฮ์ได้</p>
          )}

          <Button onClick={fetchRandom} disabled={loading} className="w-full" variant="outline">
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            สุ่มอายะฮ์ใหม่
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  );
}

export default RandomVerseDialog;
